import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import useAuthStore from '@store/useAuthStore';
import API from '@/api/api';
import { t } from '@/utils/translator';
// import i18n from '@/i18n';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import RemainingDays from '@/components/utils/RemainingDays';
import { Button, Dropdown, Form, Upload } from 'antd';
import { Row, Col } from 'antd';
import {
  Card,
  Typography,
  Table,
  Modal,
  Tag,
  message,
  Popconfirm,
  Descriptions,
  Tabs,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  SwapLeftOutlined,
  DownSquareFilled,
  UploadOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import BasicLayout from '@layout/basic';
import TextEditor from '@common/TextEditor';
import SiteEditModal from '@/components/user/site-details/modals/SiteEditModal';
import SitePhotoModal from '@/components/user/site-details/modals/SitePhotoModal';
import { useSiteEstimationData } from '@/hooks/useSiteEstimationData';
import {
  ProjectStatusCodeColor,
  ProjectStatusCode,
  StatusColor,
  CycleStatus,
  CycleStatusUpdateMessage,
} from '@constants/status';
import { formatDateWithTimezone } from '@/utils/dateFormatter';
import { STYLE_CONSTANTS } from '@constants/siteData';

dayjs.extend(duration);

export default function AdminSiteEstimation() {
  const { project_id, site_id, estimate_site_info_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const project_code = searchParams.get('project_code');
  const site_name = searchParams.get('site_name');
  const site_pgnm = searchParams.get('site_pgnm');

  // 데이터 로직을 모두 커스텀 훅으로 위임
  const {
    loading,
    projectInfo,
    siteInfo,
    projectDocuments,
    siteEstimates,
    refetch,
    refetchEstimates,
  } = useSiteEstimationData(project_id, site_id, project_code, estimate_site_info_id);

  // UI 상태는 컴포넌트에 유지
  const [isShowCycleFeedbackEditor, showCycleFeedbackEditor] = useState(false);
  const [CycleFeedback, setCycleFeedback] = useState(null);
  const [isAbleToAddNewSiteCycle, setAbleToAddNewSiteCycle] = useState(false);
  const [isLoadingToAddNewSiteCycle, setAddNewSiteCycleLoading] = useState(false);
  const [IsShowFeedbackEditingModal, showFeedbackEditingModal] = useState(false);
  const [Editingfeedback, setEditingfeedback] = useState(null);
  const [FeedbackAttachmentInfo, setFeedbackAttachmentInfo] = useState({
    document_name: null,
    attachment_name: null,
    attachment_info_id: null,
  });

  const [isShowSiteEditModal, setSiteEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [IsSiteDeleteLoading, setSiteDeleteLoading] = useState(false);

  const [attachmentUploadkey, setAttachmentUploadkey] = useState(null);
  const [isAttachmentUploading, setAttachmentUploading] = useState(false);
  const [isAttachmentDeleting, setAttachmentDeleting] = useState(null);

  const [isShowSitePhotoModal, setShowSitePhotoModal] = useState(false);
  // 산정회차 추가 버튼 활성화 관리
  useEffect(() => {
    if (
      projectInfo &&
      projectInfo?.status_code === 'STARTED' &&
      siteInfo &&
      siteInfo?.site_status === 'STARTED'
    ) {
      if (siteEstimates.length && siteEstimates?.[0]?.cycle_status === 'FEEDBACK') {
        setAbleToAddNewSiteCycle(true);
      } else {
        setAbleToAddNewSiteCycle(false);
      }
    } else {
      setAbleToAddNewSiteCycle(false);
    }
  }, [projectInfo, siteInfo, siteEstimates]);

  async function saveDocumentFeedback({ init = false } = {}) {
    try {
      await API.post(
        `/admin/estimates/feedback/attachments/${FeedbackAttachmentInfo.attachment_info_id}`,
        {
          writer_id: user.user_id,
          comment: init ? null : Editingfeedback,
        }
      );
    } catch (error) {
      message.error('피드백 저장중 장애가 발생했습니다.');
    } finally {
      initFeedbackModal();
      await refetchEstimates(); // 회차 정보만 리프레시
    }
  }

  function initFeedbackModal() {
    showFeedbackEditingModal(false);
    setEditingfeedback(null);
    setFeedbackAttachmentInfo({
      document_name: null,
      attachment_name: null,
      attachment_info_id: null,
    });
  }
  /*** 사업장 사진 업로드 관련 ***/
  const [sitePhotoForm] = Form.useForm();

  /*** 사업장 수정 및 삭제 관련 ***/
  const [siteEditForm] = Form.useForm();
  const openSiteInfoEditModal = (_Site) => {
    siteEditForm.setFieldsValue({
      site_name: _Site.site_name,
      site_country: _Site.site_country,
      site_location: _Site.site_location,
      site_clients: _Site.site_clients,
    });
    setSiteEditModal(true);
  };

  const closeSiteInfoEditModal = () => {
    setSiteEditModal(false);
    siteEditForm.setFieldsValue({});
  };

  const initSiteInfoEditModal = () => {
    siteEditForm.setFieldsValue({
      site_name: null,
      site_country: null,
      site_address: null,
      site_clients: [],
    });
  };

  const saveSiteEditedNewSite = async (siteData, siteId) => {
    try {
      const { site_name, site_country, site_location, site_clients } = siteData;
      await API.put(`/sites/${siteId}`, {
        company_id: siteInfo?.company?.id,
        site_name: site_name,
        site_location,
        site_country,
        site_clients,
      });
      await refetch(siteId);
    } catch (error) {
      console.error('Error saving site:', error);
      throw error;
    }
  };

  const saveSiteHandler = async () => {
    try {
      // 폼 검증 실행
      await siteEditForm.validateFields();
      const editedSite = siteEditForm.getFieldsValue();

      await saveSiteEditedNewSite(editedSite, siteInfo?.id);

      setSiteEditModal(false);
      initSiteInfoEditModal();
    } catch (error) {
      console.error('Error saving site:', error);
      message.error('사업장 정보 수정 중 장애가 발생했습니다.');
    }
  };

  const deleteSite = async (is_delete_site) => {
    try {
      setSiteDeleteLoading(true);
      await API.delete(`/admin/sites/${siteInfo?.id}`, {
        params: { project_id: projectInfo?.id, is_delete_site },
      });
      message.success(
        is_delete_site
          ? `${siteInfo.site_name} 사업장의 "${projectInfo.project_name_ko}" 프로젝트 내 산정 데이터 삭제를 완료하였습니다. `
          : `${siteInfo.site_name} 사업장의 전체 데이터를 삭제 완료하였습니다.`
      );
      setShowDeleteModal(false);
      message.success(
        `"${projectInfo?.project_name_ko} 프로젝트 - 사업장 목록" 페이지로 이동합니다.`
      );
      setTimeout(() => {
        navigate(
          `/admin/estimates/projects/${projectInfo?.id}?project_code=${projectInfo?.project_code}`
        );
      }, 1000);
    } catch (error) {
      console.log(error);
      message.error('사업장 삭제 도중 장애가 발생했습니다.');
    } finally {
      setSiteDeleteLoading(false);
    }
  };
  /*** 사업장 수정 및 삭제 관련 ***/

  /*** 파일 관련 핸들러 ***/
  // 파일 업로드 핸들러
  const handleUpload = async (cycle_id, documentType, file) => {
    // 파일 크기 체크 (300MB 제한)
    const maxSize = 300 * 1024 * 1024; // 300MB in bytes
    if (file.size > maxSize) {
      message.error(t('파일 크기는 300MB를 초과할 수 없습니다'));
      return false;
    }

    // 필수 정보 체크
    if (!documentType || !cycle_id) {
      message.error(t('업로드에 필요한 정보가 부족합니다'));
      return false;
    }

    setAttachmentUploading(true);
    setAttachmentUploadkey(`cycles-${cycle_id}-documents-${documentType}`);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('site_cycle_id', cycle_id);
      formData.append('document_type', documentType);

      const response = await API.post('/attachments/site/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000, // 10분 타임아웃
      });

      if (response.data) {
        message.success(t('파일이 성공적으로 업로드되었습니다'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = t('파일 업로드 중 오류가 발생했습니다');

      if (error.code === 'ECONNABORTED') {
        errorMessage = t('업로드 시간이 초과되었습니다. 파일 크기를 확인해주세요');
      } else if (error.response?.status === 413) {
        errorMessage = t('파일 크기가 너무 큽니다');
      } else if (error.response?.status === 415) {
        errorMessage = t('지원되지 않는 파일 형식입니다');
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      message.error(errorMessage);
    } finally {
      await refetchEstimates();
      setAttachmentUploading(false);
      setAttachmentUploadkey(null);
    }

    return false;
  };

  // 파일 삭제 핸들러
  const handleDelete = async (attachmentId) => {
    setAttachmentDeleting(attachmentId);
    setShowDeleteModal(true);

    try {
      await API.delete(`/attachments/attachment/${attachmentId}`);
      message.success(t('파일이 성공적으로 삭제되었습니다'));
    } catch (error) {
      console.error('Delete error:', error);
      let errorMessage = t('파일 삭제 중 오류가 발생했습니다');

      if (error.response?.status === 404) {
        errorMessage = t('파일을 찾을 수 없습니다');
      } else if (error.response?.status === 403) {
        errorMessage = t('파일을 삭제할 권한이 없습니다');
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      message.error(errorMessage);
    } finally {
      await refetchEstimates();
      setAttachmentDeleting(null);
      setShowDeleteModal(false);
    }
  };
  /*** 파일 관련 핸들러 ***/

  function CycleAttachments(cycle_id, cycle_status, attachments) {
    return (
      <>
        <Tabs
          type="card"
          tabBarStyle={{ marginBottom: 0 }}
          items={projectDocuments.map((ProjectDocument) => {
            const type_name = ProjectDocument?.document_type?.type_name_ko;
            const type_code = ProjectDocument?.document_type?.type_code;
            const document_type_id = ProjectDocument?.document_type_id;
            const ProjectAttachments = attachments[type_code];
            return {
              label: `${type_name}(${type_code})`,
              key: `Estimates-cycle-${cycle_id}-Document-${type_code}-${document_type_id}`,
              children: (
                <div className="border py-3 px-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="mb-0">증빙서류별 피드백</h3>
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => handleUpload(cycle_id, type_code, file)}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg,.png"
                    >
                      <Tooltip
                        title={t(
                          '업로드 가능한 파일 형식: PDF, DOC, DOCX, XLS, XLSX, XLSM, JPG, JPEG, PNG (최대 300MB)'
                        )}
                        placement="bottom"
                      >
                        <Button
                          // size="small"
                          icon={<UploadOutlined />}
                          disabled={cycle_status == 'DONE' || isAttachmentUploading}
                          loading={
                            isAttachmentUploading &&
                            attachmentUploadkey === `cycles-${cycle_id}-documents-${type_code}`
                          }
                        >
                          {isAttachmentUploading &&
                          attachmentUploadkey === `cycles-${cycle_id}-documents-${type_code}`
                            ? t('업로드 중...')
                            : t('첨부하기')}
                        </Button>
                      </Tooltip>
                    </Upload>
                  </div>

                  <Table
                    dataSource={ProjectAttachments}
                    rowKey="attachment_id"
                    pagination={{ position: ['bottomRight'] }}
                    bordered
                    style={{ width: '100%' }}
                    tableLayout="fixed"
                    size="small"
                  >
                    <Table.Column
                      title={<span className={'fw-medium'}>{t('증빙서류')} </span>}
                      dataIndex="filename"
                      key="filename"
                      width={'30%'}
                      render={(_, record) => (
                        <div className="d-inline-flex text-ellipsis">
                          <a
                            href={`${import.meta.env.VITE_STATIC_URL}/${record?.s3_key || ''}`}
                            className="work-break"
                            target="_blank"
                            title={record.filename || t('첨부파일')}
                            download
                          >
                            {record.filename}
                          </a>

                          <Popconfirm
                            title={t('파일을 삭제하시겠습니까?')}
                            onConfirm={() => handleDelete(record.attachment_id)}
                            okText={t('삭제')}
                            cancelText={t('취소')}
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<DeleteOutlined />}
                              loading={isAttachmentDeleting === record.attachment_id}
                              disabled={isAttachmentDeleting !== null}
                              danger
                            />
                          </Popconfirm>
                        </div>
                      )}
                    />
                    <Table.Column
                      title={<span className={'fw-medium'}>{t('업로드 일자')} </span>}
                      dataIndex="attachment_info_created_at"
                      key="attachment_info_created_at"
                      width={'17.5%'}
                      align="center"
                      render={(attachment_info_created_at) => (
                        <>
                          <div className="">
                            {formatDateWithTimezone(
                              attachment_info_created_at,
                              null,
                              'YYYY년 MM월 DD일 HH시 MM분'
                            )}
                          </div>
                        </>
                      )}
                    />
                    <Table.Column
                      title={<span className={'fw-medium'}>{t('피드백')}</span>}
                      dataIndex="feedback"
                      key="feedback"
                      align="center"
                      width={'52.5%'}
                      render={(_, record) => (
                        <>
                          {!record.feedback_comment && (
                            <div className="mb-2"> 작성된 피드백이 없습니다.</div>
                          )}
                          {record.feedback_comment && (
                            <div
                              className="text-start bg-gray-1 border-gray-3 fs-6 p-2 mb-2"
                              dangerouslySetInnerHTML={{ __html: record.feedback_comment }}
                            />
                          )}
                          <Button
                            onClick={() =>
                              setAttachmentFeedback(
                                type_name,
                                record.filename,
                                record.estimate_attachment_info_id,
                                record.feedback_comment
                              )
                            }
                            disabled={cycle_status == 'DONE'}
                          >
                            피드백
                            {!record.feedback_comment && ' 작성'}
                            {record.feedback_comment && ' 수정'}
                          </Button>
                        </>
                      )}
                    />
                  </Table>
                </div>
              ),
            };
          })}
        />
      </>
    );
  }

  function setAttachmentFeedback(
    type_name,
    filename,
    estimate_attachment_info_id,
    feedback_comment
  ) {
    setFeedbackAttachmentInfo({
      document_name: type_name,
      attachment_name: filename,
      attachment_info_id: estimate_attachment_info_id,
    });
    setEditingfeedback(feedback_comment);
    showFeedbackEditingModal(true);
  }

  async function SwitchCycleState(estimate_site_info_id, estimate_site_cycle_info_id, status) {
    try {
      await API.put(
        `/admin/estimates/sites/${estimate_site_info_id}/cycles/${estimate_site_cycle_info_id}/status`,
        { status }
      );
      if (CycleStatusUpdateMessage[status]) {
        message.success(CycleStatusUpdateMessage[status]);
      } else {
        message.success('회차 업데이트를 완료하였습니다.');
      }
    } catch (error) {
      message.error('회차 업데이트를 실패하였습니다.');
    } finally {
      await refetch(); // 전체 데이터 리프레시
    }
  }

  async function CreateNewCycle() {
    try {
      setAddNewSiteCycleLoading(true);
      if (!isAbleToAddNewSiteCycle) {
        message.error(
          '프로젝트 진행 상태 / 소속 기업의 산정 상태 / 최신 산정 회차의 상태에 의해 신규 회차를 추가하실 수 없으십니다.'
        );
        return false;
      }
      await API.post(`/admin/estimates/sites/${estimate_site_info_id}/cycles`);
    } catch (error) {
      if (error?.response?.status == 409) {
        message.error(error?.response?.details);
      }
      message.error('신규 회차 생성에 실패했습니다.');
    } finally {
      await refetch(); // 전체 데이터 리프레시
      setAddNewSiteCycleLoading(false);
    }
  }

  const CycleFeedbackEditor = useCallback(
    (estimate_site_cycle_id, cycle_status, cycle_feedback) => {
      const isAbleToFeedback = cycle_status === 'FEEDBACK';

      if (isShowCycleFeedbackEditor && cycle_feedback.cycle_feedback_comment && !CycleFeedback) {
        setCycleFeedback(cycle_feedback.cycle_feedback_comment);
      }

      async function saveCycleFeedback({ init = false } = {}) {
        try {
          if (isAbleToFeedback) {
            await API.post(`/admin/estimates/feedback/cycles/${estimate_site_cycle_id}`, {
              writer_id: user.user_id,
              comment: init ? null : CycleFeedback,
            });
            showCycleFeedbackEditor(false);
          }
        } catch (error) {
          message.error('회차 피드백을 작성하는 도중 장애가 발생했습니다.');
        } finally {
          await refetchEstimates(); // 회차 정보만 리프레시
        }
      }

      return (
        <>
          {isAbleToFeedback && (
            <>
              <div className="d-flex justify-content-between mb-3">
                <h3 className="mb-0">회차 피드백 </h3>
                {!isShowCycleFeedbackEditor && (
                  <Button onClick={() => showCycleFeedbackEditor(true)}>
                    회차 피드백 작성하기
                  </Button>
                )}
                {isShowCycleFeedbackEditor && (
                  <div>
                    <Button
                      disabled={!cycle_feedback.cycle_feedback_id}
                      onClick={() => saveCycleFeedback({ init: true })}
                    >
                      삭제하기
                    </Button>
                    <Button
                      onClick={() => {
                        showCycleFeedbackEditor(false);
                        setCycleFeedback(null);
                      }}
                    >
                      취소하기
                    </Button>
                    <Button onClick={() => saveCycleFeedback()}>저장하기</Button>
                  </div>
                )}
              </div>
              {isShowCycleFeedbackEditor && (
                <TextEditor value={CycleFeedback} onChange={setCycleFeedback}></TextEditor>
              )}
              {!isShowCycleFeedbackEditor && !cycle_feedback.cycle_feedback_comment && (
                <div className="bg-gray-1 text-center p-2 mb-2"> 작성된 피드백이 없습니다.</div>
              )}
              {!isShowCycleFeedbackEditor && cycle_feedback.cycle_feedback_comment && (
                <div
                  className="text-start bg-gray-1 border-gray-3 fs-6 p-2 mb-2"
                  dangerouslySetInnerHTML={{ __html: cycle_feedback.cycle_feedback_comment }}
                />
              )}
              <hr />
            </>
          )}
        </>
      );
    },
    [isShowCycleFeedbackEditor, CycleFeedback, user.user_id, refetchEstimates]
  );

  return (
    <>
      <BasicLayout>
        <Modal
          title={
            !FeedbackAttachmentInfo.document_name
              ? '피드백 작성'
              : `${FeedbackAttachmentInfo.document_name}: ${FeedbackAttachmentInfo.attachment_name}`
          }
          open={IsShowFeedbackEditingModal}
          onCancel={initFeedbackModal}
          footer={
            <div className="text-end">
              <Button onClick={initFeedbackModal}>닫기</Button>
              <Button onClick={() => saveDocumentFeedback({ init: true })}>피드백 삭제</Button>
              <Button onClick={() => saveDocumentFeedback()}>저장하기</Button>
            </div>
          }
        >
          <div
            className="text-start bg-gray-1 border-gray-3 fs-6 p-2 mb-2"
            dangerouslySetInnerHTML={{ __html: Editingfeedback }}
          />
          <TextEditor value={Editingfeedback} onChange={setEditingfeedback}></TextEditor>
        </Modal>

        <Row className="mb-2">
          <Col xs={24} lg={24}>
            <div className="d-flex align-items-center justify-content-between mb-0">
              <Link
                className="text-gray-7 hover-text-blue-8 fs-6"
                to={`/admin/estimates/projects/${project_id}?project_code=${projectInfo?.project_code}&site_pgnm=${site_pgnm}`}
              >
                <SwapLeftOutlined className="me-2" />
                {projectInfo?.project_code} 프로젝트 - 사업장목록 페이지
              </Link>
              <div className="text-end">
                <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refetch()}>
                  새로고침
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <Typography.Title level={2}>사업장 SCOPE 3 산정: {site_name}</Typography.Title>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} lg={12}>
            <Card title="프로젝트 정보" bodyStyle={{ padding: '0px' }}>
              <Descriptions
                column={1}
                size="small"
                labelStyle={STYLE_CONSTANTS.descStyleHeader}
                contentStyle={STYLE_CONSTANTS.descStyleBody}
                bordered
              >
                <Descriptions.Item label={t('프로젝트명')}>
                  <Tag>{projectInfo?.id}</Tag>
                  {projectInfo?.project_name_ko}
                </Descriptions.Item>
                <Descriptions.Item label={t('프로젝트 코드')}>
                  {projectInfo?.project_code}
                </Descriptions.Item>
                <Descriptions.Item label={t('상태')}>
                  <Tag color={ProjectStatusCodeColor[projectInfo?.status_code]}>
                    {ProjectStatusCode[projectInfo?.status_code]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={t('기간')}>
                  {dayjs(projectInfo?.start_date).format('YYYY년 MM월 DD일')} ~{' '}
                  {dayjs(projectInfo?.end_date).format('YYYY년 MM월 DD일')}
                </Descriptions.Item>
                <Descriptions.Item label={t('남은 기간')}>
                  {/* {renderRemainingDays()} */}
                  {RemainingDays(projectInfo?.end_date)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              title="사업장 기본 정보"
              extra={
                <>
                  <Button
                    type="danger"
                    className="me-2"
                    icon={<DeleteOutlined />}
                    loading={IsSiteDeleteLoading}
                    onClick={() => setShowDeleteModal(true)}
                  >
                    사업장 삭제
                  </Button>

                  <Button type="primary" onClick={() => openSiteInfoEditModal(siteInfo)}>
                    사업장 정보 수정
                  </Button>
                  <Button
                    type="primary"
                    className="ms-2"
                    icon={<UploadOutlined />}
                    onClick={() => setShowSitePhotoModal(true)}
                  >
                    사업장 사진 업로드
                  </Button>
                </>
              }
              bodyStyle={{ padding: '0px' }}
            >
              <Row>
                <Col xs={24}>
                  <Descriptions
                    column={1}
                    size="small"
                    labelStyle={STYLE_CONSTANTS.descStyleHeader}
                    contentStyle={STYLE_CONSTANTS.descStyleBody}
                    bordered
                  >
                    <Descriptions.Item label={t('사업장명')}>
                      <Tag>{siteInfo?.id}</Tag>
                      {siteInfo?.site_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('소속 기업')}>
                      <Tag>{siteInfo?.company?.id}</Tag>
                      {siteInfo?.company?.company_name}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('사업장 국가')}>
                      {siteInfo?.site_country}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('사업장 주소')}>
                      {siteInfo?.site_location}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('HL 만도 거래법인')}>
                      {siteInfo?.site_clients?.join(', ')}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <SiteEditModal
          isVisible={isShowSiteEditModal}
          isEdit={true}
          form={siteEditForm}
          onSave={saveSiteHandler}
          onCancel={closeSiteInfoEditModal}
          onClose={() => setSiteEditModal(false)}
          onReset={initSiteInfoEditModal}
          requiredForm={{
            site_name: true,
            site_country: false,
            site_location: false,
            site_clients: false,
          }}
        />
        <SitePhotoModal
          isVisible={isShowSitePhotoModal}
          onClose={() => setShowSitePhotoModal(false)}
          onCancel={() => setShowSitePhotoModal(false)}
          estimateSiteInfoId={estimate_site_info_id}
          form={sitePhotoForm}
          onUploadSuccess={() => {
            // 업로드 성공 후 필요한 경우 추가 작업
          }}
          requiredForm={{
            site_overview_photo: false,
            site_layout_photo: false,
            site_process_photo: false,
          }}
        />
        {/* 파일 삭제 중 모달 */}
        <Modal
          open={isAttachmentDeleting}
          closable={!isAttachmentDeleting}
          footer={null}
          centered
          maskClosable={!isAttachmentDeleting}
          width={300}
          bodyStyle={{
            textAlign: 'center',
            padding: '40px 20px',
          }}
        >
          <div>
            <LoadingOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '20px' }} />

            {isAttachmentDeleting && (
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                {t('파일 삭제 중')}
              </div>
            )}
            <div style={{ fontSize: '14px', color: '#666' }}>{t('잠시만 기다려 주세요...')}</div>
          </div>
        </Modal>

        {/* 사업장 삭제 모달 */}
        <Modal
          title="사업장 삭제"
          open={showDeleteModal}
          onCancel={() => !IsSiteDeleteLoading && setShowDeleteModal(false)}
          closable={!IsSiteDeleteLoading}
          maskClosable={!IsSiteDeleteLoading}
          footer={
            <div className="text-right">
              <Button
                onClick={() => !IsSiteDeleteLoading && setShowDeleteModal(false)}
                className="border-gray-1 bg-orange-5 text-gray-9 hover-bg-orange-7 hover-text-gray-0"
              >
                사업장 삭제 취소
              </Button>
            </div>
          }
          centered
          // width={300}
        >
          <div className="text-center mb-4">
            <h3 className="mb-4">아래에서 사업장 삭제 모드를 선택주세요.</h3>

            {showDeleteModal && IsSiteDeleteLoading && (
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>
                {t('사업장 삭제 중')}
              </div>
            )}

            <div className="d-flex align-items-center justify-content-center">
              <Popconfirm
                title={
                  <span className="fs-6">
                    현재 프로젝트 내 사업장 참여정보 및 제출 증빙서류, 피드백 내용이 삭제됩니다.{' '}
                    <br />
                    삭제하시겠습니까?
                  </span>
                }
                onConfirm={() => deleteSite(false)}
                okText={t('삭제')}
                cancelText={t('취소')}
                okType="danger"
              >
                <Button
                  className="bg-gray-1 hover-bg-red-5 hover-border-red-7 hover-text-gray-1 me-3"
                  disabled={IsSiteDeleteLoading}
                >
                  현재 프로젝트 참여 데이더 삭제
                </Button>
              </Popconfirm>

              <Popconfirm
                title={
                  <span className="fs-6">
                    전체 프로젝트 내 사업장 기본정보, 사업장 참여정보 및 제출 증빙서류, 피드백
                    내용이 삭제됩니다. <br /> 정말로 삭제하시겠습니까?
                  </span>
                }
                onConfirm={() => deleteSite(true)}
                okText={t('삭제')}
                cancelText={t('취소')}
                okType="danger"
              >
                <Button
                  className="bg-gray-1 hover-bg-red-5 hover-border-red-7 hover-text-gray-1"
                  disabled={IsSiteDeleteLoading}
                >
                  사업장 관련 전체 데이터 삭제
                </Button>
              </Popconfirm>
            </div>
          </div>
          <div className="bg-orange-5 py-3 px-3 text-right">
            <ul className="fs-6 ms-0 ps-3 mb-0">
              <li className="mb-2">
                <span className="fw-bold me-1">현재 프로젝트 내 산정 내역 삭제:</span>
                <u>"{projectInfo?.project_name_ko}" 프로젝트</u> 내 참여 데이터(증빙서류, 피드백 등)
                삭제
              </li>
              <li>
                <span className="fw-bold me-1">사업장 전체 데이터 삭제:</span>
                현재 프로젝트 내 산정 내역외 사업장 전체 데이터 삭제
              </li>
            </ul>
          </div>
        </Modal>

        {/* 산정 회차 내역 */}
        <Row>
          <Col xs={24}>
            <Card
              title="산정 회차 내역"
              extra={
                <div className="text-end">
                  {!isAbleToAddNewSiteCycle && (
                    <div className="text-end mb-2">
                      프로젝트 또는 사업장의 상태가{' '}
                      <Tag className="me-0" color={ProjectStatusCodeColor['STARTED']}>
                        {ProjectStatusCode['STARTED']}
                      </Tag>{' '}
                      또는 최신 회차가{' '}
                      <Tag className="me-0" color={StatusColor['FEEDBACK']}>
                        {CycleStatus['FEEDBACK']}
                      </Tag>{' '}
                      일때만 "신규 회차"를 추가할 수 있습니다.
                    </div>
                  )}
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    loading={isLoadingToAddNewSiteCycle}
                    onClick={CreateNewCycle}
                    disabled={!isAbleToAddNewSiteCycle}
                  >
                    신규 회차 추가
                  </Button>
                </div>
              }
              className="shadow-sm"
            >
              <Table
                loading={loading}
                dataSource={siteEstimates}
                rowKey="cycle_index"
                pagination={{ position: ['none', 'none'] }}
                onRow={() => ({ style: { cursor: 'default' } })}
                style={{ width: '100%' }}
                className="site-estimate-table"
                tableLayout="fixed"
                bordered
              >
                <Table.Column
                  title={<span className={'fw-medium fs-6'}>{t('회차')}</span>}
                  dataIndex="cycle_index"
                  key="cycle_index"
                  align="center"
                  width={'5%'}
                  className="bg-white"
                  render={(cycle_index) => <span style={{ fontSize: '15px' }}>{cycle_index}</span>}
                />
                <Table.Column
                  title={<span className={'fw-medium fs-6'}>{t('상태')}</span>}
                  dataIndex="cycle_status"
                  key="cycle_status"
                  align="center"
                  width={'7.5%'}
                  className="bg-white"
                  render={(cycle_status, record) => (
                    <Tag
                      color={StatusColor[record.cycle_status]}
                      key={`cycle-${record.cycle_index}-${cycle_status}`}
                      className="me-0"
                    >
                      {t(CycleStatus[cycle_status])}
                    </Tag>
                  )}
                />
                <Table.Column
                  title={<span className={'fw-medium fs-6'}>{t('증빙서류 및 피드백')}</span>}
                  dataIndex="attachments"
                  key="attachments"
                  className="bg-white"
                  render={(_, record) => (
                    <>
                      {CycleFeedbackEditor(record.estimate_site_cycle_id, record.cycle_status, {
                        cycle_feedback_id: record.cycle_feedback_id,
                        cycle_feedback_comment: record.cycle_feedback_comment,
                        cycle_feedback_sended: record.cycle_feedback_sended,
                        cycle_feedback_sended_at: record.cycle_feedback_sended_at,
                        cycle_feedback_writer: record.cycle_feedback_writer,
                      })}
                      {CycleAttachments(
                        record.estimate_site_cycle_id,
                        record.cycle_status,
                        record.attachments
                      )}
                    </>
                  )}
                />
                <Table.Column
                  title={<div className={'fw-medium fs-6 text-center'}>{t('저장 및 수정')}</div>}
                  dataIndex="actions"
                  key="actions"
                  width={'15%'}
                  className="bg-white"
                  render={(_, record) => (
                    <div>
                      <Typography.Title level={5}>회차 상태관리</Typography.Title>
                      <Dropdown.Button
                        icon={<DownSquareFilled />}
                        menu={{
                          items: Object.entries(CycleStatus).map(([state_key, state_ko]) => ({
                            key: state_key,
                            label: `${state_ko} (${state_key})`,
                            selectable: record.cycle_status !== state_key,
                          })),
                          onClick: (e) => {
                            SwitchCycleState(
                              estimate_site_info_id,
                              record.estimate_site_cycle_id,
                              e.key
                            );
                          },
                        }}
                        size="large"
                      >
                        회차 상태 변경
                      </Dropdown.Button>
                    </div>
                  )}
                />
              </Table>
            </Card>
          </Col>
        </Row>
      </BasicLayout>
    </>
  );
}
